import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGUCrumbService, AGUToastService, LatestRequest } from 'agu_app_shell';
import { ItemKind, ResearchGrantMatch } from '../../models/research.models';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { DetailCrumb } from '../shared/detail-crumb';
import { FormatAwardLine, FormatClosingLine } from './grant-card.component';
import { FormatDayFirstDate } from '../shared/grant-dates';
import { SplitParagraphs } from '../shared/paragraphs';
import { ShortEligibilityLabels } from './eligibility-labels';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { LISTING_GONE_COPY, PageErrorCopy, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';

const GRANT_KIND: ItemKind = 'grant';

/** The funder's listed title, shown under the display title only when it differs. */
export function OfficialTitleLine(grant: Pick<ResearchGrantMatch, 'title' | 'officialTitle' | 'grantor'>): string | null {
    if (grant.title === grant.officialTitle) return null;
    return grant.grantor ? `Listed by ${grant.grantor} as “${grant.officialTitle}”` : `Official title: “${grant.officialTitle}”`;
}

/**
 * "11 Aug 2026" / "Not stated" — the same day-first shape the `Closes` fact
 * beside it uses. It printed month-first `Aug 11, 2026` until R-F16: an
 * `en-US` locale string, not the portal's date shape. The null/unparseable
 * fallback is `FormatDayFirstDate`'s own (review M-4), so both callers answer
 * a malformed date the same way instead of one of them throwing.
 */
export function FormatPostedLine(postedDate: string | null): string {
    return FormatDayFirstDate(postedDate);
}

/** What a loaded (or not-found) grant renders; computed once per load, off the zoneless render path. */
type GrantView = Pick<GrantDetailComponent, 'grant' | 'DeadlineLine' | 'DescriptionParagraphs'>;

/**
 * Grant Finder's detail view: one grant's full record, loaded by `ContentID`.
 * It opens on the crumb trail (`DetailCrumb`): the first ancestor is where the
 * grant was opened from (`AGUAppLocation.From`), else Grant Finder's search.
 */
@Component({
    standalone: false,
    selector: 'ra-grant-detail',
    templateUrl: './grant-detail.component.html',
    styleUrl: './grant-detail.component.scss',
})
/** The grant detail view: loads the grant for `ContentID`, reloads when the ID changes and drops responses to superseded loads. */
export class GrantDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) ContentID!: string;
    /** The host's verdict that this grant is one of the member's recommendations (`GrantFinderPageComponent.DetailIsRecommended`). */
    @Input() Recommended = false;
    /** The gone-listing panel's "See similar" (P/proto-states.js:121); the page navigates to its finder's All tab. */
    @Output() readonly SeeSimilar = new EventEmitter<void>();
    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121); the page navigates. */
    @Output() readonly BackToHome = new EventEmitter<void>();
    /** The crumb trail that replaced the back pill: "{origin} / {title}" (P/detail.html:888). */
    readonly Crumb: DetailCrumb;

    grant: ResearchGrantMatch | null = null;
    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    /** A record that is gone (P/proto-states.js:121) and a load that failed (state-copy.ts `PageErrorCopy`). */
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('this grant');

    /**
     * Computed once, right after `Load()` resolves — not a template-bound
     * getter. `FormatClosingLine` defaults `now` to `new Date()`; a getter
     * would re-evaluate that on every change-detection pass (ui-best-
     * practices §7's "never bind time-dependent method calls in templates").
     * `OnItemStateChanged` only ever touches `itemState`, never `closingDate`
     * or `closingIsRolling`, so it never needs to recompute this.
     */
    DeadlineLine = 'Not stated';

    /** Computed once after `Load()` (zoneless rule: no per-pass string splitting in a getter). */
    DescriptionParagraphs: string[] = [];

    private stateSub: Subscription | null = null;

    /** Latest-load-wins: only the newest ContentID's response may write state or clear `loading`. */
    private readonly request = new LatestRequest<string>();

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The surface's host, composition and crumb service, for the crumb trail (PD §6); absent outside a surface. */
        @Optional() host: ExplorerRaHostAdapter | null = null,
        @Optional() composition: RaCompositionState | null = null,
        @Optional() @Inject(AGUCrumbService) crumbs: AGUCrumbService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.Crumb = new DetailCrumb({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'detail', DefaultView: 'search' }, host, composition, crumbs);
    }

    ngOnInit(): void {
        this.Crumb.Start();
        void this.failures.ReadSupport(this.portalConfig);
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        this.LoadIfChanged();
    }

    ngOnChanges(): void {
        // A ContentID change while this component stays mounted reloads — whether the
        // previous load found the grant, found nothing, or failed. ngOnInit also calls
        // LoadIfChanged, and HasChanged makes the repeat call a no-op.
        this.LoadIfChanged();
    }

    private LoadIfChanged(): void {
        if (this.request.HasChanged(this.ContentID)) {
            void this.Load();
        }
    }

    ngOnDestroy(): void {
        this.Crumb.Stop();
        this.stateSub?.unsubscribe();
    }

    private async Load(key: string = this.ContentID): Promise<void> {
        this.loading = true;
        this.error = null;
        // The response is mapped to its view-model inside the fetch, so a mapper throw settles `failed`.
        const outcome = await this.request.Run(key, async (id): Promise<GrantView> => this.ToView(await this.api.GrantDetail(id)));
        // A newer ContentID owns the view (and its loading flag) now — drop this response.
        if (outcome.Status === 'stale') return;
        this.loading = false;
        if (outcome.Status === 'failed') {
            this.error = DescribeError(outcome.Error);
            this.failures.Record(outcome.Error);
            return;
        }
        this.failures.Reset();
        const view = outcome.Value;
        this.grant = view.grant;
        this.DeadlineLine = view.DeadlineLine;
        this.DescriptionParagraphs = view.DescriptionParagraphs;
        if (view.grant) {
            this.itemState.Prime(GRANT_KIND, [{ key: view.grant.contentID, itemState: view.grant.itemState }]);
            this.Crumb.Loaded(key, view.grant.title);
        }
    }

    private ToView(result: GrantDetailComponent['grant']): GrantView {
        return {
            grant: result,
            DeadlineLine: result ? FormatClosingLine(result) : 'Not stated',
            DescriptionParagraphs: result ? SplitParagraphs(result.description) : [],
        };
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        if (change.kind !== GRANT_KIND || !this.grant || change.key !== this.grant.contentID) return;
        this.grant = { ...this.grant, itemState: change.itemState };
    }

    Retry(): void {
        void this.Load(this.request.RequestedKey ?? this.ContentID);
    }

    /** QA 2026-10-05: "Recommended" means matched to the member's profile — never pinned, never merely new. */
    get IsRecommended(): boolean {
        return !!this.grant && this.Recommended;
    }

    get OfficialLine(): string | null {
        return this.grant ? OfficialTitleLine(this.grant) : null;
    }

    get MetaLine(): string | null {
        if (!this.grant) return null;
        const parts = [this.grant.grantor, this.grant.funderType].filter((p): p is string => !!p);
        return parts.length > 0 ? parts.join(' · ') : null;
    }

    get AwardLine(): string {
        return this.grant ? FormatAwardLine(this.grant) : 'Amount not stated';
    }

    get PostedLine(): string {
        return FormatPostedLine(this.grant?.postedDate ?? null);
    }

    get EligibilityLine(): string {
        const list = ShortEligibilityLabels(this.grant?.eligibility ?? []);
        return list.length > 0 ? list.join(', ') : 'Not stated';
    }

    async OnSaveToggle(): Promise<void> {
        if (!this.grant) return;
        const saved = !this.grant.itemState.saved;
        await this.itemState.Toggle(GRANT_KIND, this.grant.contentID, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966, `#saveBtn` :960), once the save has stuck.
    }

    async OnPinToggle(): Promise<void> {
        if (!this.grant) return;
        await this.itemState.Toggle(GRANT_KIND, this.grant.contentID, { pinned: !this.grant.itemState.pinned });
    }
}
